/**
 * Authentication Hooks
 *
 * Auth0-integrated React hooks for authentication state management.
 * Provides user context from Auth0 and MCP server.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import type { User } from '../../../../shared/types/api';
import { clearAllAuthState } from '@/services/auth/token-manager';

// ============================================================================
// Helper Functions
// ============================================================================

// Safe navigation hook that doesn't throw if router is not available
function useSafeNavigate() {
  try {
    return useNavigate();
  } catch {
    // Return a no-op function if router is not available
    return () => {};
  }
}

/**
 * Convert Auth0 user to our User type
 */
function auth0ToUser(auth0User: { email?: string; name?: string; sub?: string; picture?: string } | undefined): User | null {
  if (!auth0User) return null;

  // Try to get provisioned user data from localStorage
  const storedUser = localStorage.getItem('curaition_user');
  if (storedUser) {
    try {
      return JSON.parse(storedUser) as User;
    } catch {
      // Fall through to create user from Auth0 data
    }
  }

  // Create user from Auth0 data
  return {
    id: auth0User.sub || '',
    emailAddress: auth0User.email || '',
    firstName: auth0User.name?.split(' ')[0] || '',
    lastName: auth0User.name?.split(' ').slice(1).join(' ') || '',
    role: 'user',
    active: true,
    createdAt: new Date().toISOString(),
  } as User;
}

/**
 * Get user role from stored user data
 */
function getUserRole(): string {
  const storedUser = localStorage.getItem('curaition_user');
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser) as User;
      return user.role || 'user';
    } catch {
      return 'user';
    }
  }
  return 'user';
}

/**
 * Check if user has admin role (admin or super_admin)
 */
function checkIsAdmin(): boolean {
  const role = getUserRole();
  return role === 'super_admin' || role === 'admin';
}

/**
 * Check if user has super_admin role specifically
 */
function checkIsSuperAdmin(): boolean {
  return getUserRole() === 'super_admin';
}

/**
 * Get organization context from stored user data
 */
function getOrgContext(): { organizationId?: string; organizationRole?: string } {
  const storedUser = localStorage.getItem('curaition_user');
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser) as User;
      return {
        organizationId: user.organizationId,
        organizationRole: user.organizationRole,
      };
    } catch {
      return {};
    }
  }
  return {};
}

/**
 * Check if user is a portal user (has org, not an admin)
 */
function checkIsPortalUser(): boolean {
  const role = getUserRole();
  if (role === 'super_admin' || role === 'admin') return false;
  const { organizationId } = getOrgContext();
  return !!organizationId;
}

// ============================================================================
// Main Authentication Hook
// ============================================================================

export interface UseAuthOptions {
  redirectTo?: string;
  or?: 'redirect' | 'throw';
}

export interface UseAuthReturn {
  // State
  user: User | null;
  isLoaded: boolean;
  isSignedIn: boolean;
  roles: string[];  // User roles
  isAdmin: boolean;  // Helper for checking admin role (admin or super_admin)
  isSuperAdmin: boolean;  // Helper for checking super_admin role only
  isPortalUser: boolean;  // Has org membership, not an admin
  organizationId?: string;  // Organization UUID if member
  organizationRole?: string;  // owner, admin, member, viewer
  accessibleDomains: string[];  // Domains user has access to

  // Actions
  signIn: () => void;
  signOut: () => Promise<void>;
  getAccessToken: () => Promise<string>;

  // Loading states
  isSigningIn: boolean;
  isSigningOut: boolean;
}

export function useAuth(options: UseAuthOptions = {}): UseAuthReturn {
  const navigate = useSafeNavigate();
  const {
    user: auth0User,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
  } = useAuth0();

  const [isSigningOut, setIsSigningOut] = useState(false);

  // Convert Auth0 user to our User type
  const user = auth0ToUser(auth0User);
  const isAdmin = checkIsAdmin();
  const isSuperAdmin = checkIsSuperAdmin();
  const isPortalUser = checkIsPortalUser();
  const role = getUserRole();
  const orgContext = getOrgContext();

  // Handle redirect logic
  useEffect(() => {
    if (!isLoading && !isAuthenticated && options.or === 'redirect') {
      const redirectTo = options.redirectTo || '/signin';
      navigate(redirectTo, { replace: true });
    }
  }, [isLoading, isAuthenticated, options.or, options.redirectTo, navigate]);

  // Sign in handler - redirects to Auth0
  const signIn = useCallback(() => {
    loginWithRedirect();
  }, [loginWithRedirect]);

  // Sign out handler — unified logout that clears all state
  const signOut = useCallback(async (): Promise<void> => {
    setIsSigningOut(true);
    try {
      // Best-effort notify backend (fire and forget)
      try {
        const { apiClient } = await import('@/services/api/client');
        await apiClient.logout();
      } catch {
        // Continue with local logout even if backend call fails
      }

      // Clear all local auth state (tokens + user data)
      clearAllAuthState();

      // Logout from Auth0 (redirects to app origin)
      await logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });
    } finally {
      setIsSigningOut(false);
    }
  }, [logout]);

  // Get access token for API calls
  const getAccessToken = useCallback(async (): Promise<string> => {
    return getAccessTokenSilently();
  }, [getAccessTokenSilently]);

  return {
    // State
    user,
    isLoaded: !isLoading,
    isSignedIn: isAuthenticated,
    roles: role ? [role] : [],
    isAdmin,
    isSuperAdmin,
    isPortalUser,
    organizationId: orgContext.organizationId,
    organizationRole: orgContext.organizationRole,
    accessibleDomains: [], // Will be populated from backend

    // Actions
    signIn,
    signOut,
    getAccessToken,

    // Loading states
    isSigningIn: isLoading,
    isSigningOut,
  };
}

// ============================================================================
// User Hook (Clerk-compatible)
// ============================================================================

export function useUser(options: UseAuthOptions = {}): {
  user: User | null;
  isLoaded: boolean;
  isSignedIn: boolean;
} {
  const { user, isLoaded, isSignedIn } = useAuth(options);

  return {
    user,
    isLoaded,
    isSignedIn,
  };
}

// ============================================================================
// Sign In Hook (Auth0 version)
// ============================================================================

export interface UseSignInReturn {
  signIn: () => void;
  isLoading: boolean;
  error: Error | null;
  clearError: () => void;
}

export function useSignIn(): UseSignInReturn {
  const { signIn, isSigningIn } = useAuth();
  const { error } = useAuth0();
  const [localError, setLocalError] = useState<Error | null>(null);

  const clearError = useCallback(() => {
    setLocalError(null);
  }, []);

  return {
    signIn,
    isLoading: isSigningIn,
    error: error || localError,
    clearError,
  };
}

// ============================================================================
// Session Hook
// ============================================================================

export interface UseSessionReturn {
  session: { user: User } | null;
  isLoaded: boolean;
  getAccessToken: () => Promise<string>;
}

export function useSession(): UseSessionReturn {
  const { user, isLoaded, getAccessToken } = useAuth();

  return {
    session: user ? { user } : null,
    isLoaded,
    getAccessToken,
  };
}
